import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useBooking = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const updateBooking = async (bookingId, update) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: '/api/v1/booking/' + bookingId,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { ...update },
      });
      resolve(responseData);
    });
  };

  const getBookingsByEntertainmentDay = async (entertainmentId, date, showCanceled = false) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/by/entertainment/${entertainmentId}/day/${date}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: {
            showCanceled: showCanceled,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getFilteredBookingsByEntertainmentDay = async (
    entertainmentId,
    date,
    filter,
    showCanceled = false
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/by/entertainment/${entertainmentId}/day/${date}`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { filter: { ...filter } },
          params: {
            showCanceled: showCanceled,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourcesByEntertainmentsDay = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/resources/by/day/${data.date}`,
          method: 'POST',
          body: {
            branches: data.branches,
            entertainments: data.entertainments,
          },
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourcesBySingleEntertainmentDay = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/resources/by/entertainment/${data.entertainmentId}/day/${data.date}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const createTimeslotBlocker = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/timeslotBlocker',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            startTime: data.startTime,
            endTime: data.endTime,
            branchId: data.branchId,
            entertainmentId: data.entertainmentId,
            blockedSlots: data.blockedSlots,
            reason: data.reason,
            resourceIds: data.resourceIds,
            groupType: data.groupType,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const previewTimeslotBlockerSnap = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/timeslotBlocker/preview',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            startTime: data.startTime,
            endTime: data.endTime,
            entertainmentId: data.entertainmentId,
            resourceIds: data.resourceIds,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getTimeslotBlockerTimeOptions = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/timeslotBlocker/timeOptions',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            entertainmentId: data.entertainmentId,
            date: data.date,
            resourceIds: data.resourceIds,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  return {
    isLoading,
    error,
    getBookingsByEntertainmentDay,
    getFilteredBookingsByEntertainmentDay,
    getResourcesByEntertainmentsDay,
    getResourcesBySingleEntertainmentDay,
    updateBooking,
    createTimeslotBlocker,
    previewTimeslotBlockerSnap,
    getTimeslotBlockerTimeOptions,
  };
};

export default useBooking;
